import { Box, Typography } from '@mui/material';
import { find, propEq } from 'ramda';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../redux/ReduxHooks';
import { CHART_GREEN, CHART_RED } from '../../../theme/theme-colors';
import { GraphNoDataMessage } from '../../Graphs/GraphNoDataMessage';
import {
  BOOLEAN_WIDGET_TYPE,
  isElectronicSensorStreamSensorKey,
  isMotorStreamSensorKey,
  motorPortDisplayTitle,
  sensorClassNameFromStreamKey,
  streamPortFromSensorKey,
} from '../../Graphs/sensor-utils';
import {
  buildMotorChartSeriesLabelByPort,
  buildSensorChartSeriesLabelByPort,
  defaultSelectedMotorPorts,
  defaultSelectedSensorPorts,
  getMotorPickerEntries,
  getSensorPickerEntries,
  motorPortsFromStreamKeys,
  motorStreamKeysForPorts,
  sensorPortsFromStreamKeys,
  sensorStreamKeysForPorts,
} from '../../../utils/electronicsDashboardUtils';
import { portDisplayLabelMapFromWidgetOptions } from '../dashboardHistoricUtils';
import type { WidgetOptions } from '../dashboardTypes';
import { useLiveGraphKeys } from '../LiveGraphStore';
import {
  filterWidgetPorts,
  resolveHistoricOrLiveLabelMap,
  shouldShowAllMotorPortPicker,
  shouldShowAllSensorPortPicker,
  sortedPortsKey,
} from './dashboardWidgetPortUtils';
import { dedupeStreamKeysPreserveOrder } from '../../../utils/sensor-stream-tracker';
import { useChooseMotorsToolbar } from './useChooseMotorsToolbar';
import { useChooseSensorsToolbar } from './useChooseSensorsToolbar';
import { useMotorStreamPortsChange } from './useMotorStreamPortsChange';
import { useSensorStreamPortsChange } from './useSensorStreamPortsChange';
import { useDashboardCapabilities } from '../dashboardCapabilities';
import { usePersistDashboardWidgetContent } from './usePersistDashboardWidgetContent';
import { useWidgetHeader } from './WidgetHeaderContext';
import { resolveMotorMechanismWidgetTitle, resolveSensorWidgetTitle } from './widgetTitle';

/** Stream samples use 0/1 even when framework `streamData` type is `bool`. */
export function coerceBooleanValue(value: unknown): boolean | null {
  if (value === 1) return true;
  if (value === 0) return false;
  return null;
}

type BooleanPortCell = {
  port: number;
  label: string;
  streamKey: string;
  value: boolean | null;
};

function BooleanValueDisplay({ value }: { value: boolean | null }) {
  const backgroundColor = value === true ? CHART_GREEN : value === false ? CHART_RED : undefined;
  const label = value === true ? 'TRUE' : value === false ? 'FALSE' : null;

  if (!label || !backgroundColor) {
    return <GraphNoDataMessage />;
  }

  return (
    <Box
      data-testid="boolean-value-display"
      sx={{
        backgroundColor,
        // Stadium pill so short (2x1) widgets match the compact boolean design.
        borderRadius: 999,
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        containerType: 'size',
        px: 1.5,
        py: 0.5,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontWeight: 700,
          color: 'common.black',
          letterSpacing: 0.5,
          lineHeight: 1,
          fontSize: '2.125rem',
          '@supports (height: 1cqh)': {
            fontSize: 'clamp(1rem, 45cqh, 2.125rem)',
          },
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

interface BooleanWidgetProps {
  id: string;
  dataKey: string;
  sensorKeys?: string[];
  options?: WidgetOptions;
}

const BooleanWidget = ({ id, dataKey, sensorKeys, options }: BooleanWidgetProps) => {
  const { t } = useTranslation();
  const driveUnitPorts = useAppSelector((state) => state.application.driveUnitPorts);
  const projectCode = useAppSelector((state) => state.project.loadedProject?.program?.code ?? '');
  const { persistWidgetContent } = usePersistDashboardWidgetContent(id);
  const { isHistoric, canEditWidgetDataSelection } = useDashboardCapabilities();

  const isMotorBoolean = isMotorStreamSensorKey(dataKey);
  const isSensorBoolean = isElectronicSensorStreamSensorKey(dataKey);
  const sensorClassName = useMemo(
    () => (isSensorBoolean ? options?.sensorClassName ?? sensorClassNameFromStreamKey(dataKey) : null),
    [dataKey, isSensorBoolean, options?.sensorClassName]
  );

  const motorPickerEntries = useMemo(
    () => (isMotorBoolean ? getMotorPickerEntries(driveUnitPorts, projectCode) : []),
    [driveUnitPorts, isMotorBoolean, projectCode]
  );

  const sensorPickerEntries = useMemo(
    () => (isSensorBoolean && sensorClassName ? getSensorPickerEntries(driveUnitPorts, projectCode, sensorClassName) : []),
    [driveUnitPorts, isSensorBoolean, projectCode, sensorClassName]
  );

  const availableMotorPorts = useMemo(() => motorPickerEntries.map((entry) => entry.port), [motorPickerEntries]);
  const availableSensorPorts = useMemo(() => sensorPickerEntries.map((entry) => entry.port), [sensorPickerEntries]);

  const selectedMotorPortsKey = sortedPortsKey(options?.selectedMotorPorts);
  const selectedSensorPortsKey = sortedPortsKey(options?.selectedSensorPorts);
  const layoutSensorKeysKey = useMemo(() => (sensorKeys?.length ? [...sensorKeys].sort().join('|') : ''), [sensorKeys]);

  const selectedMotorPorts = useMemo(() => {
    if (!isMotorBoolean) return [];

    const filterToAvailable = (ports: readonly number[]) => filterWidgetPorts(ports, availableMotorPorts, isHistoric);

    const fromOptions = options?.selectedMotorPorts;
    if (fromOptions?.length) {
      return filterToAvailable(fromOptions);
    }

    const fromSensorKeys = motorPortsFromStreamKeys(sensorKeys ?? [], dataKey);
    if (fromSensorKeys.length > 1) {
      const filtered = filterToAvailable(fromSensorKeys);
      if (filtered.length) return filtered;
    }
    if (fromSensorKeys.length === 1) {
      return filterToAvailable(fromSensorKeys);
    }

    const portFromKey = streamPortFromSensorKey(dataKey);
    if (portFromKey !== null) {
      const single = filterToAvailable([portFromKey]);
      if (single.length) return single;
    }

    return filterToAvailable(defaultSelectedMotorPorts(dataKey, availableMotorPorts));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options/sensorKeys read for port values; keys avoid parse-time identity churn
  }, [availableMotorPorts, dataKey, isHistoric, isMotorBoolean, layoutSensorKeysKey, selectedMotorPortsKey]);

  const selectedSensorPorts = useMemo(() => {
    if (!isSensorBoolean) return [];

    const filterToAvailable = (ports: readonly number[]) => filterWidgetPorts(ports, availableSensorPorts, isHistoric);

    const fromOptions = options?.selectedSensorPorts;
    if (fromOptions?.length) {
      return filterToAvailable(fromOptions);
    }

    const fromSensorKeys = sensorPortsFromStreamKeys(sensorKeys ?? [], dataKey);
    if (fromSensorKeys.length > 1) {
      const filtered = filterToAvailable(fromSensorKeys);
      if (filtered.length) return filtered;
    }
    if (fromSensorKeys.length === 1) {
      return filterToAvailable(fromSensorKeys);
    }

    const portFromKey = streamPortFromSensorKey(dataKey);
    if (portFromKey !== null) {
      const single = filterToAvailable([portFromKey]);
      if (single.length) return single;
    }

    return filterToAvailable(defaultSelectedSensorPorts(dataKey, availableSensorPorts));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- options/sensorKeys read for port values; keys avoid parse-time identity churn
  }, [availableSensorPorts, dataKey, isHistoric, isSensorBoolean, layoutSensorKeysKey, selectedSensorPortsKey]);

  const liveGraphKeys = useMemo((): string[] => {
    if (isHistoric && sensorKeys?.length) {
      return dedupeStreamKeysPreserveOrder(sensorKeys);
    }

    if (isMotorBoolean && selectedMotorPorts.length > 0) {
      const keys = selectedMotorPorts.map((port) => motorStreamKeysForPorts(dataKey, [port])[0]).filter((key): key is string => Boolean(key));
      return keys.length ? keys : [dataKey];
    }
    if (isSensorBoolean && selectedSensorPorts.length > 0) {
      const keys = sensorStreamKeysForPorts(dataKey, selectedSensorPorts);
      return keys.length ? keys : [dataKey];
    }
    return [dataKey];
  }, [dataKey, isHistoric, isMotorBoolean, isSensorBoolean, selectedMotorPorts, selectedSensorPorts, sensorKeys]);

  const primaryGraphKey = liveGraphKeys[0] ?? dataKey;

  const graphs = useLiveGraphKeys(liveGraphKeys);

  const historicPortDisplayLabels = useMemo(() => portDisplayLabelMapFromWidgetOptions(options), [options]);

  const motorPortLabelByPort = useMemo(
    () =>
      resolveHistoricOrLiveLabelMap(isHistoric, historicPortDisplayLabels, () =>
        isMotorBoolean ? buildMotorChartSeriesLabelByPort(driveUnitPorts, projectCode) : new Map<number, string>()
      ),
    [driveUnitPorts, historicPortDisplayLabels, isHistoric, isMotorBoolean, projectCode]
  );

  const sensorPortLabelByPort = useMemo(
    () =>
      resolveHistoricOrLiveLabelMap(isHistoric, historicPortDisplayLabels, () =>
        isSensorBoolean && sensorClassName
          ? buildSensorChartSeriesLabelByPort(driveUnitPorts, projectCode, sensorClassName)
          : new Map<number, string>()
      ),
    [driveUnitPorts, historicPortDisplayLabels, isHistoric, isSensorBoolean, projectCode, sensorClassName]
  );

  const motorPortLabel = useCallback(
    (port: number) => {
      const fromProject = motorPortLabelByPort.get(port);
      if (fromProject) return fromProject;
      if (options?.motorVariableName?.trim()) {
        return motorPortDisplayTitle(port, options.motorVariableName);
      }
      return motorPortDisplayTitle(port, '');
    },
    [motorPortLabelByPort, options?.motorVariableName]
  );

  const sensorPortLabel = useCallback(
    (port: number) => {
      const fromProject = sensorPortLabelByPort.get(port);
      if (fromProject) return fromProject;
      if (options?.sensorVariableName?.trim()) {
        return motorPortDisplayTitle(port, options.sensorVariableName);
      }
      return motorPortDisplayTitle(port, '');
    },
    [options?.sensorVariableName, sensorPortLabelByPort]
  );

  const multiPortCells = useMemo((): BooleanPortCell[] | null => {
    const streamKeyForPort = (port: number, fallback: string): string => {
      if (isHistoric && sensorKeys?.length) {
        const fromLayout = sensorKeys.find((key) => streamPortFromSensorKey(key) === port);
        if (fromLayout) return fromLayout;
      }
      return fallback;
    };

    if (isMotorBoolean && selectedMotorPorts.length > 1) {
      return selectedMotorPorts.map((port) => {
        const streamKey = streamKeyForPort(port, motorStreamKeysForPorts(dataKey, [port])[0] ?? dataKey);
        const graph = find(propEq(streamKey, 'key'), graphs);
        const latest = graph?.data?.at(-1);
        return {
          port,
          label: motorPortLabel(port),
          streamKey,
          value: coerceBooleanValue(latest?.[streamKey]),
        };
      });
    }

    if (isSensorBoolean && selectedSensorPorts.length > 1) {
      return selectedSensorPorts.map((port) => {
        const streamKey = streamKeyForPort(port, sensorStreamKeysForPorts(dataKey, [port])[0] ?? dataKey);
        const graph = find(propEq(streamKey, 'key'), graphs);
        const latest = graph?.data?.at(-1);
        return {
          port,
          label: sensorPortLabel(port),
          streamKey,
          value: coerceBooleanValue(latest?.[streamKey]),
        };
      });
    }

    return null;
  }, [
    dataKey,
    graphs,
    isHistoric,
    isMotorBoolean,
    isSensorBoolean,
    motorPortLabel,
    selectedMotorPorts,
    selectedSensorPorts,
    sensorKeys,
    sensorPortLabel,
  ]);

  const singleGraph = useMemo(() => {
    if (multiPortCells) return undefined;
    return find(propEq(primaryGraphKey, 'key'), graphs);
  }, [graphs, multiPortCells, primaryGraphKey]);

  const singleBoolValue = useMemo(() => {
    if (multiPortCells) return null;
    const latest = singleGraph?.data?.at(-1);
    return coerceBooleanValue(latest?.[primaryGraphKey]);
  }, [multiPortCells, primaryGraphKey, singleGraph?.data]);

  const handleMotorPortsChange = useMotorStreamPortsChange({
    dataKey,
    driveUnitPorts,
    projectCode,
    streamKind: 'motor',
    persistWidgetContent,
  });

  const handleSensorPortsChange = useSensorStreamPortsChange({
    dataKey,
    persistWidgetContent,
    sensorClassName,
  });

  const chooseMotorsToolbar = useChooseMotorsToolbar({
    show: isMotorBoolean && shouldShowAllMotorPortPicker(options, motorPickerEntries),
    isHistoricMode: isHistoric,
    motors: motorPickerEntries,
    selectedPorts: selectedMotorPorts,
    onSelectedPortsChange: handleMotorPortsChange,
    disabled: !canEditWidgetDataSelection,
    selectedPortsKey: selectedMotorPortsKey,
  });

  const chooseSensorsToolbar = useChooseSensorsToolbar({
    show: isSensorBoolean && shouldShowAllSensorPortPicker(options, sensorPickerEntries),
    isHistoricMode: isHistoric,
    sensors: sensorPickerEntries,
    selectedPorts: selectedSensorPorts,
    onSelectedPortsChange: handleSensorPortsChange,
    disabled: !canEditWidgetDataSelection,
    selectedPortsKey: selectedSensorPortsKey,
  });

  const headerTitle = useMemo(() => {
    if (isMotorBoolean) {
      const motorTitle = resolveMotorMechanismWidgetTitle(BOOLEAN_WIDGET_TYPE, dataKey, t, options, {
        selectedMotorPorts,
        motorPortLabel: (port) => motorPortLabel(port),
      });
      if (motorTitle !== undefined) return motorTitle;
    }
    if (isSensorBoolean) {
      return resolveSensorWidgetTitle(BOOLEAN_WIDGET_TYPE, dataKey, options, {
        selectedSensorPorts,
        sensorPortLabel: (port) => sensorPortLabel(port),
      });
    }
    if (singleGraph?.title) return singleGraph.title;
    return dataKey;
  }, [
    dataKey,
    isMotorBoolean,
    isSensorBoolean,
    motorPortLabel,
    options,
    selectedMotorPorts,
    selectedSensorPorts,
    sensorPortLabel,
    singleGraph?.title,
    t,
  ]);

  const headerToolbar = chooseMotorsToolbar ?? chooseSensorsToolbar;

  useWidgetHeader({ title: headerTitle, toolbarEnd: headerToolbar }, [headerTitle, headerToolbar]);

  if (multiPortCells) {
    return (
      <Box
        className="widget-body"
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          gap: 2,
          width: '100%',
          height: '100%',
          p: 2,
          alignContent: 'start',
        }}
      >
        {multiPortCells.map((cell) => (
          <Box key={cell.streamKey} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600, textAlign: 'center' }}>
              {cell.label}
            </Typography>
            <BooleanValueDisplay value={cell.value} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box className="widget-body" display="flex" alignItems="center" justifyContent="center" height="100%" px={1.5} py={1}>
      <Box sx={{ width: '100%', height: '100%', maxHeight: 280, display: 'flex', minHeight: 0 }}>
        <BooleanValueDisplay value={singleBoolValue} />
      </Box>
    </Box>
  );
};

export default BooleanWidget;
